import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { signInWithEmail, signInWithStrava, signUpWithEmail } from '../../auth/actions';
import { StravaConnectButton } from '../../components/StravaConnectButton';
import styles from './SignInPage.module.css';

export type AuthMode = 'sign-in' | 'sign-up';

const COPY = {
  'sign-in': { eyebrow: 'WELCOME BACK', title: 'SIGN IN', submit: 'SIGN IN →', other: 'NEW HERE? CREATE ACCOUNT ↗' },
  'sign-up': { eyebrow: 'NEW HERE', title: 'CREATE ACCOUNT', submit: 'CREATE ACCOUNT →', other: 'HAVE AN ACCOUNT? SIGN IN ↗' },
} as const;

interface Values { name: string; email: string; password: string }

interface TextFieldProps {
  label: string;
  type: 'text' | 'email' | 'password';
  value: string;
  autoComplete: string;
  onChange: (value: string) => void;
}

function TextField({ label, type, value, autoComplete, onChange }: TextFieldProps) {
  return (
    <label className={styles.field}>
      <span className={styles.label}>{label}</span>
      <input
        className={styles.input} type={type} value={value} autoComplete={autoComplete} required
        minLength={type === 'password' ? 8 : undefined} onChange={(e) => onChange(e.currentTarget.value)}
      />
    </label>
  );
}

function Fields({ mode, values, onChange }: { mode: AuthMode; values: Values; onChange: (next: Values) => void }) {
  return (
    <>
      {mode === 'sign-up' && (
        <TextField label="NAME" type="text" autoComplete="name" value={values.name} onChange={(name) => onChange({ ...values, name })} />
      )}
      <TextField label="EMAIL" type="email" autoComplete="email" value={values.email} onChange={(email) => onChange({ ...values, email })} />
      <TextField
        label="PASSWORD" type="password" value={values.password}
        autoComplete={mode === 'sign-up' ? 'new-password' : 'current-password'}
        onChange={(password) => onChange({ ...values, password })}
      />
    </>
  );
}

type Via = 'email' | 'strava';

export function AuthForm({ mode, initialError }: { mode: AuthMode; initialError: string | null }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [values, setValues] = useState<Values>({ name: '', email: '', password: '' });
  const submitEmail = (): ReturnType<typeof signInWithEmail> =>
    (mode === 'sign-up' ? signUpWithEmail(values) : signInWithEmail(values.email, values.password));
  const auth = useMutation({
    mutationFn: async (via: Via): Promise<Via> => {
      const result = await (via === 'strava' ? signInWithStrava() : submitEmail());
      if (!result.ok) throw new Error(result.message);
      return via;
    },
    onSuccess: async (via) => {
      if (via === 'strava') return;
      queryClient.clear();
      await navigate({ to: mode === 'sign-up' ? '/onboarding/ftp' : '/' });
    },
  });
  // A Strava start that succeeded is redirecting away, so the buttons stay busy.
  const busy = auth.isPending || (auth.isSuccess && auth.data === 'strava');
  const error = auth.isIdle ? initialError : (auth.error?.message ?? null);
  const copy = COPY[mode];

  return (
    <form className={styles.form} onSubmit={(event) => { event.preventDefault(); auth.mutate('email'); }} aria-labelledby="auth-title">
      <div className={styles.head}>
        <p className={styles.eyebrow}>{copy.eyebrow}</p>
        <h2 id="auth-title" className={styles.title}>{copy.title}</h2>
      </div>
      <div className={styles.strava}><StravaConnectButton onClick={() => auth.mutate('strava')} busy={busy} /></div>
      <div className={styles.divider}><span>OR EMAIL</span></div>
      <Fields mode={mode} values={values} onChange={setValues} />
      {error !== null && <p role="alert" className="alert-hot">{error}</p>}
      <button type="submit" className="btn btn-ink btn-block" disabled={busy}>{copy.submit}</button>
      <Link to="/sign-in" search={mode === 'sign-up' ? {} : { mode: 'sign-up' }} className={styles.switch}>{copy.other}</Link>
    </form>
  );
}
